import React from "react";
import { Button } from "../../../../../ui/components/Button";
import { Badge } from "../../../../../ui/components/Badge";
import { ToggleGroup } from "../../../../../ui/components/ToggleGroup";
import { DropdownMenu } from "../../../../../ui/components/DropdownMenu";
import {
  FeatherChevronDown,
  FeatherChevronLeft,
  FeatherChevronRight,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { useRecentExpenseActivity } from "../../hooks/useRecentExpenseActivity";
import type { RecentExpenseActivityItemEntity } from "../../../domain/entities/RecentExpenseActivityEntity";
import { truncateText } from "../../../../../shared/utils/formatters";
import { getRecentActivityDateRangeSubtext } from "../../utils/recentRevenueActivityDateRange";

export type ActivityPeriod = "last7d" | "last30d" | "last90d" | "custom";
export type ActivityFilter = "all" | "paid" | "pending" | "overdue";

function getPeriodLabel(
  period: ActivityPeriod,
  customStartDate?: string,
  customEndDate?: string
): string {
  switch (period) {
    case "last7d":
      return "Last 7 days";
    case "last30d":
      return "Last 30 days";
    case "last90d":
      return "Last 90 days";
    case "custom":
      if (customStartDate && customEndDate) {
        try {
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} – ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
          }
        } catch {
          // fall through
        }
      }
      return "Custom Range";
    default:
      return "Last 30 days";
  }
}

export function RecentExpenseActivitySection() {
  const {
    period,
    onPeriodChange,
    showCustomRange,
    customStartDate,
    customEndDate,
    onCustomStartDateChange,
    onCustomEndDateChange,
    onCustomApply,
    onCustomRangeCancel,
    filter,
    onFilterChange,
    rows,
    loading,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
    error,
  } = useRecentExpenseActivity();

  const statusVariant = (s: RecentExpenseActivityItemEntity["status"]) =>
    s === "paid" ? "success" : s === "overdue" ? "error" : "warning";
  const statusLabel = (s: RecentExpenseActivityItemEntity["status"]) =>
    s === "paid" ? "Paid" : s === "overdue" ? "Overdue" : "Pending";

  const displayRows = rows;

  const dateRangeSubtext = getRecentActivityDateRangeSubtext(
    period ?? "last30d",
    customStartDate,
    customEndDate,
  );

  const handleCustomApply = () => {
    if (customStartDate && customEndDate) {
      onCustomApply(customStartDate, customEndDate);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Recent Expense Activity
          </span>
          <span className="text-body font-body text-subtext-color">
            {dateRangeSubtext}
          </span>
        </div>
        {showCustomRange ? (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => onCustomStartDateChange(e.target.value)}
              className="rounded-md border border-neutral-border bg-default-background px-3 py-1.5 text-sm text-default-font"
              aria-label="Start date"
            />
            <span className="text-body font-body text-subtext-color">to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => onCustomEndDateChange(e.target.value)}
              className="rounded-md border border-neutral-border bg-default-background px-3 py-1.5 text-sm text-default-font"
              aria-label="End date"
            />
            <Button
              variant="brand-primary"
              size="small"
              onClick={handleCustomApply}
              disabled={!customStartDate || !customEndDate}
            >
              Apply
            </Button>
            <Button
              variant="neutral-secondary"
              size="small"
              onClick={onCustomRangeCancel}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <SubframeCore.DropdownMenu.Root>
            <SubframeCore.DropdownMenu.Trigger asChild>
              <Button
                variant="neutral-secondary"
                size="small"
                iconRight={<FeatherChevronDown />}
              >
                {getPeriodLabel(period ?? "last30d", customStartDate, customEndDate)}
              </Button>
            </SubframeCore.DropdownMenu.Trigger>
            <SubframeCore.DropdownMenu.Portal>
              <SubframeCore.DropdownMenu.Content
                side="bottom"
                align="end"
                sideOffset={4}
                asChild
              >
                <DropdownMenu>
                  <DropdownMenu.DropdownItem
                    icon={null}
                    onClick={() => onPeriodChange("last7d")}
                  >
                    Last 7 days
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    icon={null}
                    onClick={() => onPeriodChange("last30d")}
                  >
                    Last 30 days
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    icon={null}
                    onClick={() => onPeriodChange("last90d")}
                  >
                    Last 90 days
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    icon={null}
                    onClick={() => onPeriodChange("custom")}
                  >
                    Custom Range
                  </DropdownMenu.DropdownItem>
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Content>
            </SubframeCore.DropdownMenu.Portal>
          </SubframeCore.DropdownMenu.Root>
        )}
      </div>
      <ToggleGroup
        value={filter}
        onValueChange={(v: string) =>
          (v === "all" || v === "paid" || v === "pending" || v === "overdue") &&
          onFilterChange(v as ActivityFilter)
        }
      >
        <ToggleGroup.Item icon={null} value="all">
          All
        </ToggleGroup.Item>
        <ToggleGroup.Item icon={null} value="paid">
          Paid
        </ToggleGroup.Item>
        <ToggleGroup.Item icon={null} value="pending">
          Pending
        </ToggleGroup.Item>
        <ToggleGroup.Item icon={null} value="overdue">
          Overdue
        </ToggleGroup.Item>
      </ToggleGroup>
      {error ? (
        <div className="text-body font-body text-error-600 py-2">{error}</div>
      ) : null}
      {loading ? (
        <div className="flex w-full flex-col gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex w-full items-center gap-4 border-b border-neutral-border px-4 py-3 h-12 animate-pulse rounded bg-neutral-100"
            />
          ))}
        </div>
      ) : displayRows.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center py-12 text-center">
          <span className="text-body font-body text-subtext-color">
            No bills found for this period.
          </span>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start">
          <div className="flex w-full items-center gap-4 border-b border-solid border-neutral-border px-4 py-3">
            <span className="w-24 flex-none text-caption-bold font-caption-bold text-subtext-color">
              Date
            </span>
            <span className="grow flex-none text-caption-bold font-caption-bold text-subtext-color">
              Vendor
            </span>
            <span className="w-40 flex-none text-caption-bold font-caption-bold text-subtext-color">
              Category
            </span>
            <span className="w-32 flex-none text-caption-bold font-caption-bold text-subtext-color text-right">
              Amount
            </span>
            <span className="w-32 flex-none text-caption-bold font-caption-bold text-subtext-color">
              Status
            </span>
            <span className="w-28 flex-none text-caption-bold font-caption-bold text-subtext-color">
              Due Date
            </span>
            <div className="flex w-12 flex-none items-start" />
          </div>
          {displayRows.map((row, i) => (
            <div
              key={row.id ?? i}
              className="flex w-full items-center gap-4 border-b border-solid border-neutral-border px-4 py-3"
            >
              <span className="w-24 flex-none text-body font-body text-default-font">
                {row.date}
              </span>
              <span className="grow flex-none text-body font-body text-default-font" title={row.vendor}>
                {truncateText(row.vendor, 25)}
              </span>
              <span className="w-40 flex-none text-body font-body text-default-font" title={row.category}>
                {truncateText(row.category, 25)}
              </span>
              <span className="w-32 flex-none text-body-bold font-body-bold text-default-font text-right">
                {row.amount}
              </span>
              <div className="w-32 flex-none">
                <Badge variant={statusVariant(row.status)}>
                  {statusLabel(row.status)}
                </Badge>
              </div>
              <span className="w-28 flex-none text-body font-body text-subtext-color">
                {row.dueDate}
              </span>
              <div className="flex w-12 flex-none items-center justify-end" />
            </div>
          ))}
        </div>
      )}
      {totalPages > 1 && (
        <div className="flex w-full items-center justify-between mt-4">
          <span className="text-body font-body text-subtext-color">
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="neutral-tertiary"
              size="small"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              icon={<FeatherChevronLeft />}
            >
              Previous
            </Button>
            <Button
              variant="neutral-tertiary"
              size="small"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              icon={<FeatherChevronRight />}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
